/**
 * Static deploy-time table of models available on each terminal cluster.
 *
 * SOURCE OF TRUTH: `infra/helm/terminal/environments/<env>.yaml`
 * `envConfig.BEDROCK_MODEL_IDS`. When that changes, mirror it here.
 *
 * Why this lives in the UI: OpenClaw exposes model switching only via the
 * `/model <id>` slash command — not a structured `models.list` RPC — so
 * there's nothing on the wire to enumerate. We hand-mirror the Helm
 * values; the picker sends the matching id via `/model <id>` on pick.
 */

import type { ModelInfo } from './types';

const MODELS_BY_HOST_SUFFIX: Readonly<Record<string, readonly ModelInfo[]>> = {
  // infra/helm/terminal/environments/terminal-paradigm-prod.yaml
  'terminal.paradigm.tech': [
    { id: 'eu.anthropic.claude-sonnet-4-6', label: 'Sonnet 4.6' },
    {
      id: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0',
      label: 'Haiku 4.5',
    },
    { id: 'eu.anthropic.claude-opus-4-7', label: 'Opus 4.7', legacy: true },
  ],
  // infra/helm/terminal/environments/terminal-prod.yaml (DIME cluster, JP region)
  'terminal.dime.gg': [
    { id: 'jp.anthropic.claude-opus-4-8', label: 'Opus 4.8' },
    { id: 'jp.anthropic.claude-opus-4-7', label: 'Opus 4.7', legacy: true },
    { id: 'jp.anthropic.claude-sonnet-4-6', label: 'Sonnet 4.6' },
    {
      id: 'jp.anthropic.claude-haiku-4-5-20251001-v1:0',
      label: 'Haiku 4.5',
    },
  ],
};

export function getStaticModels(agentUrl: string | null): readonly ModelInfo[] {
  if (agentUrl == null || agentUrl.length === 0) return [];
  let host: string;
  try {
    host = new URL(agentUrl).hostname;
  } catch {
    return [];
  }
  for (const [suffix, models] of Object.entries(MODELS_BY_HOST_SUFFIX)) {
    // Anchored suffix match — `evilterminal.paradigm.tech` must not match
    // `terminal.paradigm.tech`; only exact or proper-subdomain matches.
    if (host === suffix || host.endsWith(`.${suffix}`)) return models;
  }
  // Unknown host (custom Lightsail, dev forks) — return empty rather
  // than guess a list. Each cluster's `BEDROCK_MODEL_IDS` is
  // region-pinned (jp.* vs eu.*); guessing the wrong region would have
  // the picker offer ids the gateway will reject. The picker hides
  // itself on empty.
  return [];
}

// Flat lookup of every known Bedrock id → its friendly label, across
// all clusters. Used by `rewriteModelMentions` so the gateway's verbose
// "Model set to amazon-bedrock/<id> for this session." chat replies can
// be displayed as "Model set to Sonnet 4.6 for this session." in the UI.
const ALL_LABELS_BY_ID: ReadonlyMap<string, string> = new Map(
  Object.values(MODELS_BY_HOST_SUFFIX).flatMap((models) =>
    models.map((m) => [m.id, m.label] as const),
  ),
);

export function getModelLabel(modelId: string): string {
  return ALL_LABELS_BY_ID.get(modelId) ?? modelId;
}

/**
 * True if `text` looks like the gateway's reply to `/model <id>` — used
 * to suppress the duplicate when the picker already added an optimistic
 * acknowledgment client-side.
 *
 * TODO(openclaw): brittle — depends on gateway wording staying as
 * `Model set to <provider>/<id> for this session.` / `Model reset to
 * default (...)`. Replace with a structured frame when the gateway
 * exposes one.
 */
export function isModelSwitchEcho(text: string): boolean {
  return (
    text.startsWith('Model set to amazon-bedrock/') ||
    text.startsWith('Model reset to default')
  );
}

/**
 * Cosmetic rewrites applied to assistant text at render time:
 *
 *   1. `amazon-bedrock/<bedrock-id>` → friendly label (e.g. `Sonnet 4.6`).
 *      Unknown ids pass through unchanged.
 *   2. OpenClaw's `/model` help output is one long line with `Label:
 *      value` sections — break each onto its own paragraph and bold the
 *      label, and wrap `/model[s]` slash commands in backticks. Gated on
 *      the input looking like a model-related message so normal prose
 *      isn't reformatted.
 *
 * Chat state stays untouched — this only affects the rendered string.
 */
export function rewriteModelMentions(text: string): string {
  const isModelText =
    text.includes('amazon-bedrock/') || /^Current:\s/u.test(text);
  let out = text.replace(
    /amazon-bedrock\/([\w.:-]+)/gu,
    (match, id: string) => ALL_LABELS_BY_ID.get(id) ?? match,
  );
  if (isModelText) {
    out = out.replace(/^(Current|Switch|Runtime|Browse|More):\s/u, '**$1:** ');
    out = out.replace(
      /\s(Current|Switch|Runtime|Browse|More):\s/gu,
      '\n\n**$1:** ',
    );
    out = out.replace(
      /(\/models?(?:\s+<[^>]+>)*(?:\s+--\w+(?:\s+<[^>]+>)?)?(?:\s+status)?)/gu,
      '`$1`',
    );
  }
  return out;
}
